import React from 'react';
import { useQuery } from 'react-query';
import { api } from '../services/api';
import { Sun, Backpack, AlertTriangle } from 'lucide-react';

/**
 * The morning note.
 *
 * The owner's ask (Sep 16 2026): "Add a note in the morning for the crew and
 * admin that says what jobs they have today and if they need to pack any extra
 * equipment for an extra job."
 *
 * Same endpoint for both — the server decides what a crew login is allowed to
 * see (their own routes, no money, no office errands).
 */
export default function DayBriefCard({ compact = false }: { compact?: boolean }) {
  const { data } = useQuery('day-brief', () => api.get('/day/brief').then(r => r.data), {
    staleTime: 300000,
    retry: false,
  });
  if (!data || !data.note) return null;
  const pack: { kit: string; why: string }[] = data.pack || [];
  const down: boolean = /(^|\s)Down:/.test(String(data.note));

  return (
    <section className="rounded-3xl bg-brand-600 text-black shadow-brand p-5 sm:p-6">
      <div className="flex items-center gap-2">
        <Sun className="h-5 w-5" aria-hidden />
        <p className="text-xs font-black uppercase tracking-wider">{data.headline}</p>
      </div>
      <p className="mt-2 text-[15px] sm:text-base font-semibold leading-relaxed">{data.note}</p>
      {!compact && pack.length > 0 && (
        <div className="mt-4 rounded-2xl bg-white/90 text-gray-900 p-4 shadow-soft">
          <div className="flex items-center gap-2 mb-2">
            <Backpack className="h-4 w-4 text-brand-700" aria-hidden />
            <p className="text-xs font-black uppercase tracking-wider text-brand-800">Pack this</p>
          </div>
          <ul className="space-y-1.5">
            {pack.map(p => (
              <li key={p.kit} className="text-[15px]">
                <span className="font-bold">{p.kit}</span> <span className="text-gray-700">for {p.why}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {down && (
        <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-black text-snow px-3 py-1.5 text-xs font-bold">
          <AlertTriangle className="h-4 w-4 text-amber-400" aria-hidden /> Something is reported down. Check before you load.
        </p>
      )}
    </section>
  );
}
